<script setup lang="ts">
import { ref, onMounted, computed, watch } from 'vue';
import { useOrganizationsStore, type Repository, type PipelineInfo } from '../stores/organizations';
import type { UIRefType } from '../types';
import RefSelector from './RefSelector.vue';

const props = withDefaults(defineProps<{
  initialRepositoryId?: string;
  initialBranch?: string;
  initialPipeline?: string;
  initialRefType?: UIRefType;
}>(), {
  initialRepositoryId: '',
  initialBranch: '',
  initialPipeline: '',
});

const emit = defineEmits<{
  submit: [{
    repositoryId: string;
    branch: string;
    pipeline?: string;
    refType?: 'branch' | 'tag' | 'pull_request';
  }];
  cancel: [];
}>();

const organizationsStore = useOrganizationsStore();

const repositories = ref<Repository[]>([]);
const selectedRepositoryId = ref(props.initialRepositoryId);
const branch = ref(props.initialBranch);
const selectedRefType = ref<UIRefType | null>(props.initialRefType ?? null);
const selectedPipeline = ref<string>('');
const loading = ref(false);
const loadingRepos = ref(true);
const loadingPipelines = ref(false);

// Pipeline state
const pipelines = ref<PipelineInfo[]>([]);
const configExists = ref(false);
const pipelineError = ref<string | undefined>();
const pipelineNotFound = ref<string | undefined>(); // Track if initial pipeline doesn't exist

// Filter to only show enabled repositories in dropdown
const enabledRepositories = computed(() =>
  repositories.value.filter((r) => r.buildEnabled)
);

// Get selected repository for default values
const selectedRepo = computed(() =>
  repositories.value.find((r) => r.id === selectedRepositoryId.value)
);

// Get org slug for the selected repository
const selectedOrgSlug = computed(() => {
  if (!selectedRepo.value) return null;
  const org = organizationsStore.organizations.find(
    (o) => o.id === selectedRepo.value!.organizationId
  );
  return org?.slug ?? null;
});

// Pipelines that would run (sorted: matching first)
const sortedPipelines = computed(() => {
  return [...pipelines.value].sort((a, b) => {
    if (a.wouldRun && !b.wouldRun) return -1;
    if (!a.wouldRun && b.wouldRun) return 1;
    return 0;
  });
});

// Auto-select the first matching pipeline
const autoSelectedPipeline = computed(() => {
  const matching = pipelines.value.filter((p: PipelineInfo) => p.wouldRun);
  return matching.length === 1 ? matching[0].name : null;
});

// The effective pipeline to use (only returns value if explicitly or auto-selected)
const effectivePipeline = computed(() => {
  return selectedPipeline.value || autoSelectedPipeline.value || undefined;
});

// Get ref type - only returns a value if explicitly known from dropdown selection
function getRefType(): UIRefType | null {
  // Only return a type if user selected from dropdown (where we know the actual type)
  // If user typed manually, we don't guess - let the API handle it without a refType
  return selectedRefType.value;
}

// Fetch pipelines when repo or branch changes
async function fetchPipelinesForRef() {
  if (!selectedRepo.value || !selectedOrgSlug.value) {
    pipelines.value = [];
    configExists.value = false;
    pipelineError.value = undefined;
    pipelineNotFound.value = undefined;
    return;
  }

  const targetRef = branch.value.trim() || selectedRepo.value.defaultBranch;
  // For pipeline preview, use known type or default to 'branch'
  const refType = selectedRefType.value ?? 'branch';

  loadingPipelines.value = true;
  pipelineNotFound.value = undefined;
  try {
    const result = await organizationsStore.fetchPipelines(
      selectedOrgSlug.value,
      selectedRepo.value.id,
      targetRef,
      refType
    );
    pipelines.value = result.pipelines;
    configExists.value = result.configExists;
    pipelineError.value = result.error;

    // Try to select the initial pipeline if provided and not yet selected
    if (props.initialPipeline && !selectedPipeline.value) {
      const found = result.pipelines.find((p: PipelineInfo) => p.name === props.initialPipeline);
      if (found) {
        selectedPipeline.value = props.initialPipeline;
      } else if (result.pipelines.length > 0) {
        // Pipeline doesn't exist anymore - show warning
        pipelineNotFound.value = props.initialPipeline;
      }
    }

    // Reset selection if the selected pipeline no longer exists
    if (selectedPipeline.value && !result.pipelines.find((p: PipelineInfo) => p.name === selectedPipeline.value)) {
      selectedPipeline.value = '';
    }
  } finally {
    loadingPipelines.value = false;
  }
}

// When repository changes, reset branch, refType, and pipelines
watch(selectedRepositoryId, (newId, oldId) => {
  if (newId !== oldId) {
    branch.value = '';
    selectedRefType.value = null;
    selectedPipeline.value = '';
    pipelines.value = [];
    configExists.value = false;
    pipelineError.value = undefined;
  }
});

// Debounced pipeline fetch when branch changes
let pipelineFetchTimeout: ReturnType<typeof setTimeout> | null = null;
watch([() => selectedRepo.value?.id, branch], () => {
  if (pipelineFetchTimeout) clearTimeout(pipelineFetchTimeout);
  pipelineFetchTimeout = setTimeout(fetchPipelinesForRef, 300);
});

onMounted(async () => {
  loadingRepos.value = true;
  repositories.value = await organizationsStore.fetchAllAccessibleRepositories();
  loadingRepos.value = false;
});

function handleSubmit() {
  if (!selectedRepositoryId.value) return;

  loading.value = true;

  const targetRef = branch.value.trim() || selectedRepo.value?.defaultBranch || 'main';
  const refType = getRefType();
  // Map 'pr' to 'pull_request' for API compatibility, undefined if not known
  const apiRefType = refType === 'pr' ? 'pull_request' : refType ?? undefined;

  emit('submit', {
    repositoryId: selectedRepositoryId.value,
    branch: targetRef,
    pipeline: effectivePipeline.value,
    refType: apiRefType,
  });

  // Reset form
  setTimeout(() => {
    loading.value = false;
  }, 500);
}
</script>

<template>
  <form @submit.prevent="handleSubmit" class="build-form">
    <h2>Create New Build</h2>

    <div class="form-group">
      <label for="repository">Repository</label>
      <select
        id="repository"
        v-model="selectedRepositoryId"
        required
        :disabled="loadingRepos"
      >
        <option value="" disabled>
          {{ loadingRepos ? 'Loading repositories...' : 'Select a repository' }}
        </option>
        <option
          v-for="repo in enabledRepositories"
          :key="repo.id"
          :value="repo.id"
        >
          {{ repo.fullPath }}
        </option>
      </select>
    </div>

    <div class="form-group">
      <label for="branch">Branch</label>
      <RefSelector
        v-model="branch"
        :repository="selectedRepo ?? null"
        :placeholder="selectedRepo?.defaultBranch || 'main'"
        @update:ref-type="selectedRefType = $event"
      />
      <span v-if="selectedRepo?.defaultBranch" class="hint">
        Default: {{ selectedRepo.defaultBranch }}
      </span>
    </div>

    <div class="form-group pipeline-group">
      <label>Pipeline</label>
      <div v-if="loadingPipelines" class="pipeline-loading">
        <div class="spinner-small"></div>
        <span>Loading pipelines...</span>
      </div>

      <div v-else-if="pipelineError" class="pipeline-error">
        <span class="error-icon">⚠</span>
        <span>{{ pipelineError }}</span>
      </div>

      <div v-else-if="!configExists && selectedRepo" class="pipeline-warning">
        <span class="warning-icon">⚠</span>
        <span>No <code>.kiln.yml</code> found in repository</span>
      </div>

      <div v-else-if="pipelines.length === 0 && selectedRepo" class="pipeline-info">
        <span>Select a branch to see available pipelines</span>
      </div>

      <template v-else-if="pipelines.length > 0">
        <div v-if="pipelineNotFound" class="pipeline-warning">
          <span class="warning-icon">⚠</span>
          <span>Pipeline "<strong>{{ pipelineNotFound }}</strong>" no longer exists. Please select another.</span>
        </div>

        <div class="pipeline-list">
        <div
          v-for="pipeline in sortedPipelines"
          :key="pipeline.name"
          class="pipeline-item"
          :class="{
            selected: selectedPipeline === pipeline.name || (!selectedPipeline && autoSelectedPipeline === pipeline.name),
            'would-run': pipeline.wouldRun,
            'would-not-run': !pipeline.wouldRun,
          }"
          @click="selectedPipeline = pipeline.name"
        >
          <div class="pipeline-header">
            <input
              type="radio"
              :id="`pipeline-${pipeline.name}`"
              :value="pipeline.name"
              v-model="selectedPipeline"
              :checked="selectedPipeline === pipeline.name || (!selectedPipeline && autoSelectedPipeline === pipeline.name)"
            />
            <label :for="`pipeline-${pipeline.name}`" class="pipeline-name">
              {{ pipeline.name }}
            </label>
            <span v-if="pipeline.wouldRun" class="pipeline-badge will-run">will run</span>
            <span v-else class="pipeline-badge wont-run">won't run</span>
          </div>
          <div v-if="pipeline.runReason" class="pipeline-reason">
            {{ pipeline.runReason }}
          </div>
        </div>
      </div>
      </template>

      <div v-else class="pipeline-info">
        <span>Select a repository to see pipelines</span>
      </div>
    </div>

    <div class="form-actions">
      <button type="button" class="btn-secondary" @click="emit('cancel')">
        Cancel
      </button>
      <button
        type="submit"
        class="btn-primary"
        :disabled="loading || !selectedRepositoryId || !effectivePipeline"
        :title="!effectivePipeline ? 'Please select a pipeline' : ''"
      >
        {{ loading ? 'Creating...' : 'Create Build' }}
      </button>
    </div>
  </form>
</template>

<style scoped>
.build-form {
  background: var(--color-card-bg);
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 1px 3px var(--color-card-shadow);
  border: 1px solid var(--color-card-border);
}

h2 {
  margin: 0 0 20px;
  font-size: 1.25rem;
  color: var(--color-text-primary);
}

.form-group {
  margin-bottom: 16px;
}

/* When the form lives in a modal (see NewBuildModal), the pipeline list
   can grow beyond the viewport. Let the pipeline group claim whatever
   vertical space is left and scroll its own list internally, so the
   Cancel/Create buttons below always stay visible.
   The `min-height: 0` is the standard flexbox escape hatch that lets a
   flex child shrink below its intrinsic content height. */
.pipeline-group {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.pipeline-group > .pipeline-list {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  /* A touch of padding on the right so the scrollbar doesn't crowd the
     pipeline item borders. */
  padding-right: 4px;
}

label {
  display: block;
  margin-bottom: 6px;
  font-weight: 500;
  font-size: 0.9rem;
  color: var(--color-text-primary);
}

input,
textarea,
select {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid var(--color-input-border);
  border-radius: 6px;
  font-size: 0.95rem;
  font-family: inherit;
  background: var(--color-input-bg);
  color: var(--color-input-text);
}

input:focus,
textarea:focus,
select:focus {
  outline: none;
  border-color: var(--color-input-focus-border);
  box-shadow: 0 0 0 3px var(--color-input-focus-ring);
}

select {
  background-color: var(--color-input-bg);
  cursor: pointer;
}

select:disabled {
  background-color: var(--color-bg-tertiary);
  cursor: not-allowed;
}

textarea {
  resize: vertical;
  font-family: monospace;
}

.hint {
  display: block;
  margin-top: 4px;
  font-size: 0.8rem;
  color: var(--color-text-secondary);
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 20px;
}

.btn-primary,
.btn-secondary {
  padding: 10px 20px;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-primary {
  background: var(--color-btn-primary-bg);
  color: var(--color-btn-primary-text);
  border: none;
}

.btn-primary:hover:not(:disabled) {
  background: var(--color-btn-primary-hover);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-secondary {
  background: var(--color-btn-secondary-bg);
  color: var(--color-btn-secondary-text);
  border: 1px solid var(--color-btn-secondary-border);
}

.btn-secondary:hover {
  background: var(--color-btn-secondary-hover);
}

/* Pipeline styles */
.pipeline-loading {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--color-text-secondary);
  font-size: 0.9rem;
  padding: 8px 0;
}

.spinner-small {
  width: 14px;
  height: 14px;
  border: 2px solid var(--color-border-secondary);
  border-top-color: var(--color-accent);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.pipeline-error,
.pipeline-warning {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9rem;
  padding: 8px 12px;
  border-radius: 6px;
}

.pipeline-error {
  background: var(--color-error-bg, #fef2f2);
  color: var(--color-error-text, #b91c1c);
}

.pipeline-warning {
  background: var(--color-warning-bg, #fffbeb);
  color: var(--color-warning-text, #92400e);
}

.pipeline-info {
  font-size: 0.9rem;
  color: var(--color-text-secondary);
  padding: 8px 0;
}

.pipeline-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.pipeline-item {
  border: 1px solid var(--color-border-secondary);
  border-radius: 6px;
  padding: 10px 12px;
  cursor: pointer;
  transition: all 0.15s;
}

.pipeline-item:hover {
  border-color: var(--color-accent);
}

.pipeline-item.selected {
  border-color: var(--color-accent);
  background: var(--color-accent-bg, rgba(59, 130, 246, 0.05));
}

.pipeline-item.would-not-run {
  opacity: 0.7;
}

.pipeline-header {
  display: flex;
  align-items: center;
  gap: 8px;
}

.pipeline-header input[type="radio"] {
  width: auto;
  margin: 0;
}

.pipeline-name {
  font-weight: 500;
  font-family: monospace;
  font-size: 0.9rem;
  cursor: pointer;
  flex: 1;
}

.pipeline-badge {
  font-size: 0.7rem;
  padding: 2px 6px;
  border-radius: 4px;
  font-weight: 500;
  text-transform: uppercase;
}

.pipeline-badge.will-run {
  background: var(--color-success-bg);
  color: var(--color-success-text);
}

.pipeline-badge.wont-run {
  background: var(--color-badge-default-bg);
  color: var(--color-text-secondary);
}

.pipeline-reason {
  font-size: 0.8rem;
  color: var(--color-text-secondary);
  margin-top: 4px;
  margin-left: 24px;
}
</style>
